<script lang="ts">
  import type { DonorTier } from '../lib/stores/auth';

  // Donor-tier marker shown next to every username on the site. Four
  // designs share a globe motif so the icons read as a family — only the
  // color and embellishment change as the tier rises:
  //
  //   free     — faint outline (logged-in, no contribution yet)
  //   one_time — filled dim-accent globe (made a one-time contribution)
  //   pro      — filled accent globe with a halo ring (active subscriber)
  //   lifetime — filled bright globe topped by a star (lifetime donor)
  //
  // All four render at the requested size from the same 24×24 viewBox
  // so they line up visually wherever the icon is placed. Mirror this
  // file when you change the SVGs in
  // packages/api/resources/views/filament/tables/columns/donor-tier-icon.blade.php
  // (the admin panel can't read these CSS vars, so it inlines hex colors).

  interface Props {
    tier: DonorTier;
    size?: number;
  }

  let { tier, size = 16 }: Props = $props();

  const ARIA_LABEL: Record<DonorTier, string> = {
    free: 'Free account',
    one_time: 'One-time supporter',
    pro: 'Pro supporter',
    lifetime: 'Lifetime supporter',
  };
</script>

<span
  class="donor-icon donor-icon--{tier}"
  aria-label={ARIA_LABEL[tier]}
  title={ARIA_LABEL[tier]}
>
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
    {#if tier === 'free'}
      <!-- Faint placeholder globe — neutral, communicates "logged in, not a donor". -->
      <circle cx="12" cy="12" r="9" fill="var(--ink-faint)" />
      <ellipse cx="12" cy="12" rx="9" ry="3.5" fill="none" stroke="rgba(0,0,0,0.25)" stroke-width="0.6" />
      <line x1="12" y1="3" x2="12" y2="21" stroke="rgba(0,0,0,0.25)" stroke-width="0.6" />

    {:else if tier === 'one_time'}
      <!-- Filled dim-accent globe — same proportions as the free
           outline so the visible disc stays the same diameter across
           tiers (was r=7.5, smaller than free's r=9 — players noticed
           the inconsistency on the leaderboard). Color alone now
           differentiates one-time from free; pro still adds the halo
           and lifetime keeps its star above. -->
      <circle cx="12" cy="12" r="9" fill="var(--accent-dim)" />
      <ellipse cx="12" cy="12" rx="9" ry="3.5" fill="none" stroke="rgba(0,0,0,0.25)" stroke-width="0.6" />
      <line x1="12" y1="3" x2="12" y2="21" stroke="rgba(0,0,0,0.25)" stroke-width="0.6" />

    {:else if tier === 'pro'}
      <!-- Halo ring + filled accent globe. Ring is hairline to keep the
           icon legible at 16px without crowding. -->
      <circle cx="12" cy="12" r="11" fill="none" stroke="var(--accent)" stroke-width="1" stroke-opacity="0.45" />
      <circle cx="12" cy="12" r="7.5" fill="var(--accent)" />
      <ellipse cx="12" cy="12" rx="7.5" ry="3" fill="none" stroke="rgba(0,0,0,0.3)" stroke-width="0.5" />
      <line x1="12" y1="4.5" x2="12" y2="19.5" stroke="rgba(0,0,0,0.3)" stroke-width="0.5" />

    {:else}
      <!-- Lifetime: brighter globe pushed down to make room for a star
           crown. Star sits cleanly above the globe with a small gap so
           both shapes read at small sizes. -->
      <polygon
        points="12,1 12.7,3 14.9,3.1 13.1,4.4 13.8,6.4 12,5.2 10.2,6.4 10.9,4.4 9.1,3.1 11.3,3"
        fill="var(--accent-bright)"
      />
      <circle cx="12" cy="15" r="7" fill="var(--accent-bright)" />
      <ellipse cx="12" cy="15" rx="7" ry="2.6" fill="none" stroke="rgba(0,0,0,0.3)" stroke-width="0.5" />
      <line x1="12" y1="8" x2="12" y2="22" stroke="rgba(0,0,0,0.3)" stroke-width="0.5" />
    {/if}
  </svg>
</span>
